/**
 * LocationActionButtons Component
 *
 * Renders call-to-action buttons for restaurant reservations, ordering, and waitlist.
 * Consolidates common button rendering logic used in both soft opening and normal phases.
 *
 * Shows:
 * - Reservation button (top) - links to reservation platforms
 * - Waitlist button (bottom) - for open locations accepting walk-ins
 * - Ordering button (bottom) - links to ordering platforms
 * - Catering info (bottom) - when location offers catering
 *
 * Reusable across LocationHero and LocationsList contexts.
 */

import Button from '@/components/Button/Button'
import JoinWaitlist from '@/components/JoinWaitlist'
import OrderDropdown from '@/components/OrderDropdown/OrderDropdown'
import { ParsedResponse } from '@/types/yelp'
import { getFirstAvailableWaitTime } from '@/utils/yelp'
import useI18n from '@/hooks/use-i18n'
import styles from './LocationActionButtons.module.scss'

type LocationActionButtonsProps = {
  reservationLinks?: SanityLocationServiceLinkType[]
  serviceLinks?: SanityLocationServiceLinkType[]
  businessId?: string
  isReservationOnly: boolean
  waitlistData?: ParsedResponse | null
  isLoadingWaitlist: boolean
  isMobile: boolean | null
  hasCatering: boolean
  cateringLink?: SanityLink
  // Optional props for list context
  dropdownPosition?: 'bottomLeft' | 'bottomRight'
  slug?: string
  className?: string
}

const LocationActionButtons = ({
  reservationLinks,
  serviceLinks,
  businessId,
  hasCatering,
  cateringLink,
  isReservationOnly,
  waitlistData,
  isLoadingWaitlist,
  isMobile,
  dropdownPosition = 'bottomLeft',
  slug,
  className,
}: LocationActionButtonsProps) => {
  const { i18n } = useI18n()
  const hasReservationLinks = reservationLinks && reservationLinks.length > 0
  const hasServiceLinks = serviceLinks && serviceLinks.length > 0
  const hasWaitlist = waitlistData?.waitEstimates
  const showCatering = hasCatering && !!cateringLink
  const shouldShowBottomButtons = (isLoadingWaitlist || hasWaitlist || showCatering) && businessId && !isReservationOnly

  // Don't render if no buttons to show
  if (!hasReservationLinks && !shouldShowBottomButtons) {
    return null
  }

  return (
    <div className={`${styles.buttons} ${className || ''}`.trim()}>
      {/* Top buttons: Reservation and Order */}
      {hasReservationLinks && (
        <div className={styles.topButtons}>
          <OrderDropdown
            className={styles.reserveButton}
            serviceLinks={reservationLinks}
            primary
            isReserve
            dropdownPosition={dropdownPosition}
            slug={slug}
          />
          {hasServiceLinks && (
            <OrderDropdown
              className={styles.orderButton}
              serviceLinks={serviceLinks}
              dropdownPosition={isMobile ? 'bottomRight' : dropdownPosition}
              slug={slug}
            />
          )}
        </div>
      )}

      {/* Bottom buttons: Waitlist + Catering */}
      {shouldShowBottomButtons && (
        <div className={styles.bottomButtons}>
          {businessId && !isReservationOnly && (
            <JoinWaitlist
              waitlistTime={getFirstAvailableWaitTime(waitlistData?.waitEstimates)}
              secondary
              className={styles.joinWaitlistButton}
              businessId={businessId}
              waitlistStatus={isLoadingWaitlist ? 'LOADING' : waitlistData?.waitlistStatus}
              closedReason={waitlistData?.closedReason}
            />
          )}
          {showCatering && (
            <Button
              className={styles.catering}
              link={cateringLink}
              secondary
            >
              <span className={styles.catering__title}>{i18n('catering')}</span>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

LocationActionButtons.displayName = 'LocationActionButtons'

export default LocationActionButtons
